import { useState, useEffect, useRef } from "react"
import { useLocation } from "wouter"
import { useQuery, useMutation } from "@tanstack/react-query"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Card, CardContent } from "@/components/ui/card"
import { Textarea } from "@/components/ui/textarea"
import { Input } from "@/components/ui/input"
import { useToast } from "@/hooks/use-toast"
import { apiRequest, queryClient } from "@/lib/queryClient"
import {
  Check,
  X,
  Edit2,
  Hash,
  Calendar as CalendarIcon,
  Loader2,
  AlertCircle,
  TestTube2,
  Clock,
  CalendarClock,
  Star,
  RotateCw,
  ChevronRight,
} from "lucide-react"
import { Switch } from "@/components/ui/switch"
import { Label } from "@/components/ui/label"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { Calendar } from "@/components/ui/calendar"
import { getSuggestedTimes, hasConflict } from "@/lib/postingSchedule"
import { FaLinkedin, FaFacebook, FaInstagram } from "react-icons/fa"
import { format } from "date-fns"
import { de } from "date-fns/locale"
import type { ContentUpload, GeneratedPost } from "@shared/schema"

interface PhotoPreviewModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  upload: ContentUpload
}

const platformConfig = {
  linkedin: {
    name: "LinkedIn",
    icon: FaLinkedin,
    color: "bg-blue-600",
    textColor: "text-blue-600",
    borderColor: "border-blue-600"
  },
  facebook: {
    name: "Facebook",
    icon: FaFacebook,
    color: "bg-blue-500",
    textColor: "text-blue-500",
    borderColor: "border-blue-500"
  },
  instagram: {
    name: "Instagram",
    icon: FaInstagram,
    color: "bg-pink-500",
    textColor: "text-pink-500",
    borderColor: "border-pink-500"
  }
}

// Per-platform content length ceilings (characters). Used for the live editor counter.
const PLATFORM_LIMITS: Record<string, number> = {
  linkedin: 3000,
  facebook: 63206,
  instagram: 2200,
}

const statusConfig = {
  pending: {
    label: "Ausstehend",
    color: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
  },
  approved: {
    label: "Freigegeben",
    color: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
  },
  rejected: {
    label: "Abgelehnt",
    color: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
  },
  scheduled: {
    label: "Geplant",
    color: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
  },
  posted: {
    label: "Veröffentlicht",
    color: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
  }
}

export function PhotoPreviewModal({ open, onOpenChange, upload }: PhotoPreviewModalProps) {
  const { toast } = useToast()
  const [, setLocation] = useLocation()
  const [activeTab, setActiveTab] = useState<string>("linkedin")
  const [editingPost, setEditingPost] = useState<string | null>(null)
  const [editContent, setEditContent] = useState<string>("")
  const [editHashtags, setEditHashtags] = useState<string>("")
  
  // Test Mode state (persisted in localStorage, scoped to the photo module — slice-4)
  const [testMode, setTestMode] = useState<boolean>(() => {
    try {
      return localStorage.getItem('lateApiTestMode:photo') === 'true'
    } catch {
      return false
    }
  })
  
  // Persist test mode to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('lateApiTestMode:photo', testMode.toString())
    } catch {
      /* storage blocked */
    }
  }, [testMode])

  // Scheduling state
  const [scheduleOpen, setScheduleOpen] = useState(false)
  const [customTimeOpen, setCustomTimeOpen] = useState(false)
  const [customDate, setCustomDate] = useState<Date | undefined>(undefined)
  const [customHour, setCustomHour] = useState("12")
  const [customMinute, setCustomMinute] = useState("00")

  // Schedule mutation
  const schedulePostMutation = useMutation({
    mutationFn: async ({ postId, platform, scheduledFor }: { postId: string; platform: string; scheduledFor: Date }) => {
      return apiRequest("POST", `/api/generated-posts/${postId}/publish`, {
        isDraft: false,
        platform,
        scheduledFor: scheduledFor.toISOString(),
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/generated-posts', upload.id] })
      queryClient.invalidateQueries({ queryKey: ['/api/generated-posts'] })
      setScheduleOpen(false)
      setCustomTimeOpen(false)
      toast({
        title: "Post geplant!",
        description: "Der Post wurde erfolgreich eingeplant.",
      })
    },
    onError: (error: any) => {
      if (error.status === 403 && error.data?.requiresConnection) {
        const platform = error.data.platform || 'unbekannt'
        toast({
          title: `${platform.charAt(0).toUpperCase() + platform.slice(1)} nicht verbunden`,
          description: "Bitte verbinden Sie Ihr Konto unter 'Plattform verbinden'.",
          variant: "destructive",
        })
        return
      }
      const errorDetails = error.data?.error || error.data?.message || error.message || "Unbekannter Fehler"
      toast({
        title: "Fehler beim Planen",
        description: errorDetails,
        variant: "destructive",
      })
    },
  })

  const handleSchedule = (post: GeneratedPost, scheduledFor: Date) => {
    schedulePostMutation.mutate({ postId: post.id, platform: post.platform || 'linkedin', scheduledFor })
  }

  const handleCustomSchedule = (post: GeneratedPost) => {
    if (!customDate) return
    const scheduled = new Date(customDate)
    scheduled.setHours(parseInt(customHour, 10), parseInt(customMinute, 10), 0, 0)
    const berlinStr = scheduled.toLocaleString("en-US", { timeZone: "Europe/Berlin" })
    const berlinLocal = new Date(berlinStr)
    const offset = scheduled.getTime() - berlinLocal.getTime()
    const berlinDate = new Date(scheduled.getTime() + offset)
    handleSchedule(post, berlinDate)
  }

  // Fetch scheduled posts for conflict detection
  const { data: scheduledPosts = [] } = useQuery<GeneratedPost[]>({
    queryKey: ['/api/generated-posts/scheduled'],
    queryFn: async () => {
      const response = await fetch('/api/generated-posts/scheduled')
      if (!response.ok) return []
      return response.json()
    },
    enabled: open,
    staleTime: 30000,
  })

  const scheduledTimesByPlatform = scheduledPosts.reduce((acc, post) => {
    if (post.scheduledFor) {
      const p = post.platform || 'linkedin'
      if (!acc[p]) acc[p] = []
      acc[p].push(new Date(post.scheduledFor))
    }
    return acc
  }, {} as Record<string, Date[]>)

  // Fetch generated posts for this upload
  const { data: posts = [], isLoading, error } = useQuery<GeneratedPost[]>({
    queryKey: ['/api/generated-posts', upload.id],
    queryFn: async () => {
      const response = await fetch(`/api/generated-posts?uploadId=${upload.id}`)
      if (!response.ok) throw new Error('Failed to fetch posts')
      return response.json()
    },
    enabled: open,
    staleTime: 60000 // Cache for 1 minute to avoid refetching
  })

  // Group posts by platform
  const postsByPlatform = posts.reduce((acc, post) => {
    acc[post.platform] = post
    return acc
  }, {} as Record<string, GeneratedPost>)

  // Update active tab when posts load - set to first available platform
  const availablePlatforms = Object.keys(postsByPlatform)
  
  // Effect to set initial tab to first platform with content
  useEffect(() => {
    if (posts.length > 0 && availablePlatforms.length > 0) {
      // Only update if current tab has no content
      if (!postsByPlatform[activeTab]) {
        setActiveTab(availablePlatforms[0])
      }
    }
  }, [posts.length, availablePlatforms.length, activeTab, postsByPlatform, availablePlatforms])

  // Approve/Reject mutation
  const approvePostMutation = useMutation({
    mutationFn: async ({ postId, approval }: { 
      postId: string
      approval: { approvalStatus: "approved" | "rejected"; approvalNotes?: string } 
    }) => {
      return apiRequest("POST", `/api/generated-posts/${postId}/approve`, approval)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/generated-posts', upload.id] })
      queryClient.invalidateQueries({ queryKey: ['/api/generated-posts'] })
      toast({
        title: "Post aktualisiert",
        description: "Der Post-Status wurde erfolgreich aktualisiert.",
      })
    },
    onError: (error: any) => {
      toast({
        title: "Fehler",
        description: error.message || "Beim Aktualisieren des Posts ist ein Fehler aufgetreten.",
        variant: "destructive",
      })
    }
  })

  const handleApprove = (post: GeneratedPost) => {
    approvePostMutation.mutate({
      postId: post.id,
      approval: { approvalStatus: "approved" }
    })
  }

  const handleReject = (post: GeneratedPost) => {
    approvePostMutation.mutate({
      postId: post.id,
      approval: { approvalStatus: "rejected" }
    })
  }

  // True while a publish was triggered by the OAuth auto-resume effect (not a click) —
  // its 403 handler must not redirect again, or the user loops between this modal and
  // /platform-connections for as long as the account stays unconnected.
  const autoResumeRef = useRef(false)

  // Publish mutation (Late API posting)
  const publishPostMutation = useMutation({
    mutationFn: async (postId: string) => {
      return apiRequest("POST", `/api/generated-posts/${postId}/publish`, { isDraft: testMode })
    },
    onSuccess: (data) => {
      autoResumeRef.current = false
      queryClient.invalidateQueries({ queryKey: ['/api/generated-posts', upload.id] })
      queryClient.invalidateQueries({ queryKey: ['/api/generated-posts'] })

      if (testMode) {
        toast({
          title: "Test-Post erstellt!",
          description: "Der Post wurde als Draft gespeichert (wird nicht gepostet).",
        })
      } else {
        toast({
          title: "Post veröffentlicht!",
          description: "Der Post wurde erfolgreich freigegeben.",
        })
      }
    },
    onError: (error: any) => {
      // Check if it's a "platform not connected" error
      if (error.status === 403 && error.data?.requiresConnection) {
        const { platform, redirectUrl } = error.data

        if (autoResumeRef.current) {
          // The auto-resumed publish itself failed on the still-unconnected platform —
          // do NOT redirect again (see autoResumeRef above), just say what's wrong.
          autoResumeRef.current = false
          toast({
            title: "Platform nicht verbunden",
            description: `Bitte verbinden Sie Ihr ${platform}-Konto unter 'Plattform verbinden'.`,
            variant: "destructive",
          })
          return
        }

        // Store the post ID for auto-resume after OAuth
        sessionStorage.setItem('pendingPublishPostId', error.data.postId || '')

        // Redirect to platform connection page
        toast({
          title: "Platform verbinden",
          description: `Bitte verbinden Sie Ihr ${platform}-Konto, um fortzufahren.`,
        })

        // Redirect after short delay to show toast
        setTimeout(() => {
          window.location.href = redirectUrl
        }, 1500)
      } else {
        toast({
          title: "Fehler",
          description: error.message || "Beim Veröffentlichen ist ein Fehler aufgetreten.",
          variant: "destructive",
        })
      }
    }
  })

  const handlePublish = (post: GeneratedPost) => {
    publishPostMutation.mutate(post.id)
  }

  // Auto-resume publish after OAuth return
  useEffect(() => {
    if (open && posts.length > 0) {
      const pendingPostId = sessionStorage.getItem('pendingPublishPostId')
      
      if (pendingPostId) {
        // Find the post that matches the pending ID
        const pendingPost = posts.find(p => p.id === pendingPostId)
        
        if (pendingPost) {
          // Clear the pending ID
          sessionStorage.removeItem('pendingPublishPostId')

          // Auto-trigger publish (flagged so a repeat 403 doesn't redirect-loop)
          autoResumeRef.current = true
          handlePublish(pendingPost)
        }
      }
    }
  }, [open, posts])

  // Edit mutation
  const editPostMutation = useMutation({
    mutationFn: async ({ postId, updates }: {
      postId: string
      updates: { postTitle?: string; postContent?: string; hashtags?: string[] }
    }) => {
      return apiRequest("PATCH", `/api/generated-posts/${postId}`, updates)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/generated-posts', upload.id] })
      queryClient.invalidateQueries({ queryKey: ['/api/generated-posts'] })
      setEditingPost(null)
      toast({
        title: "Änderungen gespeichert",
        description: "Der Post wurde erfolgreich aktualisiert.",
      })
    },
    onError: (error: any) => {
      toast({
        title: "Fehler",
        description: error.message || "Beim Speichern ist ein Fehler aufgetreten.",
        variant: "destructive",
      })
    }
  })

  const handleEdit = (post: GeneratedPost) => {
    setEditingPost(post.id)
    setEditContent(post.postContent || "")
    // Strip leading '#' so the editor is consistent with the '#'-free view chips;
    // handleSaveEdit re-adds '#' on save.
    setEditHashtags(post.hashtags?.map(t => t.replace(/^#/, "")).join(", ") || "")
  }

  const handleSaveEdit = (postId: string) => {
    // Parse hashtags from comma-separated string
    const hashtagsArray = editHashtags
      .split(",")
      .map(tag => tag.trim())
      .filter(tag => tag.length > 0)
      .map(tag => tag.startsWith("#") ? tag : `#${tag}`)

    editPostMutation.mutate({
      postId,
      updates: {
        postContent: editContent,
        hashtags: hashtagsArray.length > 0 ? hashtagsArray : undefined
      }
    })
  }

  const handleCancelEdit = () => {
    setEditingPost(null)
    setEditContent("")
    setEditHashtags("")
  }

  // Whether the open editor has unsaved changes (compared against the original post).
  const editingOriginal = editingPost ? posts.find(p => p.id === editingPost) : undefined
  const isDirty = !!editingOriginal && (
    editContent !== (editingOriginal.postContent ?? "") ||
    editHashtags !== (editingOriginal.hashtags?.map(t => t.replace(/^#/, "")).join(", ") ?? "")
  )

  // Reset the (modal-scoped) scheduling popover state so a date/time picked on one platform
  // never leaks into another when the user switches tabs.
  const resetScheduleState = () => {
    setScheduleOpen(false)
    setCustomTimeOpen(false)
    setCustomDate(undefined)
    setCustomHour("12")
    setCustomMinute("00")
  }

  const handleTabChange = (value: string) => {
    if (isDirty && !window.confirm("Ungespeicherte Änderungen verwerfen?")) return
    handleCancelEdit()
    resetScheduleState()
    setActiveTab(value)
  }

  const handleGuardedOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && isDirty && !window.confirm("Ungespeicherte Änderungen verwerfen?")) return
    onOpenChange(nextOpen)
  }

  const formatDate = (dateString: string | Date) => {
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString
    return format(date, 'dd.MM.yyyy HH:mm', { locale: de })
  }

  return (
    <Dialog open={open} onOpenChange={handleGuardedOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader className="flex flex-row items-center justify-between space-y-0 gap-4 pr-14">
          <DialogTitle className="truncate">Post Vorschau & Freigabe</DialogTitle>

          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center space-x-2 shrink-0">
                <Switch
                  id="test-mode"
                  checked={testMode}
                  onCheckedChange={setTestMode}
                  data-testid="switch-test-mode"
                />
                <Label htmlFor="test-mode" className="text-sm cursor-pointer whitespace-nowrap">
                  Test Modus
                </Label>
              </div>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              Test Modus: Posts werden als Entwurf gespeichert und NICHT veröffentlicht.
            </TooltipContent>
          </Tooltip>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Photo Preview */}
          <div className="space-y-4">
            <div className="relative aspect-square bg-muted rounded-lg overflow-hidden">
              <img
                src={upload.filePath}
                alt={upload.originalName}
                className="w-full h-full object-cover"
              />
            </div>
            
            <div className="space-y-2">
              <h3 className="font-medium text-sm">{upload.originalName}</h3>
              <div className="flex items-center space-x-2 text-xs text-muted-foreground">
                <CalendarIcon className="h-3 w-3" />
                <span>{formatDate(upload.createdAt || upload.updatedAt || new Date())}</span>
              </div>
              {upload.message && (
                <p className="text-sm text-muted-foreground italic">
                  "{upload.message}"
                </p>
              )}
            </div>
          </div>

          {/* Posts by Platform */}
          <div className="space-y-4">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : error ? (
              <div className="text-center py-12">
                <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
                <p className="text-sm text-muted-foreground">Fehler beim Laden der Posts</p>
              </div>
            ) : posts.length === 0 ? (
              <div className="text-center py-12">
                <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-sm text-muted-foreground">
                  Noch keine Beiträge erstellt. Ihr Team arbeitet daran.
                </p>
              </div>
            ) : (
              <Tabs value={activeTab} onValueChange={handleTabChange}>
                <TabsList className="grid w-full grid-cols-3">
                  {Object.entries(platformConfig).map(([platform, config]) => {
                    const post = postsByPlatform[platform]
                    const Icon = config.icon
                    return (
                      <TabsTrigger 
                        key={platform} 
                        value={platform}
                        disabled={!post}
                        className="gap-2"
                      >
                        <Icon className="h-4 w-4" />
                        {config.name}
                      </TabsTrigger>
                    )
                  })}
                </TabsList>

                <p className="text-xs text-muted-foreground mt-2">
                  Jede Plattform ist ein eigener Beitrag. Änderungen gelten nur für die aktive Plattform.
                </p>

                {Object.entries(platformConfig).map(([platform, config]) => {
                  const post = postsByPlatform[platform]
                  if (!post) return null

                  const effectiveStatus = (post as any).postingStatus === "scheduled" || (post as any).postingStatus === "posted"
                    ? (post as any).postingStatus
                    : post.approvalStatus
                  const status = statusConfig[effectiveStatus as keyof typeof statusConfig] || statusConfig.pending
                  const isEditing = editingPost === post.id
                  const isScheduled = (post as any).postingStatus === "scheduled"
                  const scheduledTime = (post as any).scheduledFor
                  const contentLimit = PLATFORM_LIMITS[post.platform] ?? 3000
                  const contentOver = isEditing && editContent.length > contentLimit

                  return (
                    <TabsContent key={platform} value={platform} className="space-y-4">
                      <Card>
                        <CardContent className="p-4 space-y-4">
                          {/* Status Badge */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <div className={`p-2 rounded-lg ${config.color}`}>
                                <config.icon className="h-4 w-4 text-white" />
                              </div>
                              <span className="font-medium">{config.name}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              {isScheduled ? (
                                /* Scheduled: a single clickable badge that deep-links into the
                                   Posting-Plan at exactly this post. */
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (isDirty && !window.confirm("Ungespeicherte Änderungen verwerfen?")) return
                                    onOpenChange(false)
                                    setLocation(`/veroeffentlichungsplan?post=${post.id}`)
                                  }}
                                  className="inline-flex items-center gap-1 rounded-full border border-blue-300 dark:border-blue-700 px-2.5 py-0.5 text-xs font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/40 transition-colors"
                                  title="Im Veröffentlichungsplan ansehen"
                                  data-testid="badge-scheduled-link"
                                >
                                  <Clock className="h-3 w-3" aria-hidden="true" />
                                  {scheduledTime ? `Geplant für ${format(new Date(scheduledTime), "EEE, d. MMM, HH:mm", { locale: de })}` : "Geplant"}
                                  <ChevronRight className="h-3 w-3" aria-hidden="true" />
                                </button>
                              ) : (
                                <Badge className={status.color}>
                                  {status.label}
                                </Badge>
                              )}
                            </div>
                          </div>

                          {/* Post Title — read-only, derived from the first line of the content.
                              That first line is what's used as the hook on posting; there is no
                              separate editable title, so we just show it (updates live while editing). */}
                          <div>
                            <h4 className="font-semibold text-sm text-muted-foreground mb-1">Titel</h4>
                            <p className="font-medium" data-testid="post-title-derived">
                              {(() => {
                                const src = isEditing ? editContent : post.postContent
                                return (src || "").split("\n").map(l => l.trim()).find(l => l.length > 0) || "Ohne Titel"
                              })()}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              Wird automatisch aus der ersten Zeile des Contents erzeugt.
                            </p>
                          </div>

                          {/* Post Content */}
                          <div>
                            <h4 className="font-semibold text-sm text-muted-foreground mb-1">Content</h4>
                            {isEditing ? (
                              <>
                                <Textarea
                                  value={editContent}
                                  onChange={(e) => setEditContent(e.target.value)}
                                  className="min-h-[200px] resize-none"
                                  aria-label="Post-Inhalt"
                                  data-testid="textarea-post-content"
                                />
                                <p className={`text-xs mt-1 text-right ${contentOver ? "text-destructive font-medium" : "text-muted-foreground"}`}>
                                  {editContent.length} / {contentLimit} Zeichen
                                </p>
                              </>
                            ) : (
                              <p className="text-sm whitespace-pre-wrap">{post.postContent}</p>
                            )}
                          </div>

                          {/* Hashtags */}
                          <div>
                            <h4 className="font-semibold text-sm text-muted-foreground mb-2">Hashtags</h4>
                            {isEditing ? (
                              <Input
                                value={editHashtags}
                                onChange={(e) => setEditHashtags(e.target.value)}
                                placeholder="Hashtags ohne # (getrennt durch Komma)"
                                aria-label="Hashtags"
                                data-testid="input-post-hashtags"
                              />
                            ) : (
                              post.hashtags && post.hashtags.length > 0 ? (
                                <div className="flex flex-wrap gap-2">
                                  {post.hashtags.map((tag, index) => (
                                    <Badge key={index} variant="outline" className="gap-1">
                                      <Hash className="h-3 w-3" />
                                      {tag.replace('#', '')}
                                    </Badge>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-sm text-muted-foreground">(Keine Hashtags)</p>
                              )
                            )}
                          </div>

                          {/* Test Mode Visual Indicator */}
                          {testMode && (
                            <Alert className="bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-900">
                              <TestTube2 className="h-4 w-4 text-yellow-600 dark:text-yellow-500" />
                              <AlertDescription className="text-yellow-800 dark:text-yellow-200">
                                <strong>Test Modus aktiv:</strong> Post wird als Draft gespeichert und NICHT veröffentlicht.
                              </AlertDescription>
                            </Alert>
                          )}

                          {/* Action Buttons */}
                          <div className="flex flex-wrap items-center justify-end gap-2 pt-4 border-t">
                            {isEditing ? (
                              <>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={handleCancelEdit}
                                  disabled={editPostMutation.isPending}
                                  data-testid="button-cancel-edit"
                                >
                                  <X className="h-4 w-4 mr-2" />
                                  Abbrechen
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={() => handleSaveEdit(post.id)}
                                  disabled={editPostMutation.isPending || contentOver}
                                  data-testid="button-save-edit"
                                >
                                  {editPostMutation.isPending ? (
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                  ) : (
                                    <Check className="h-4 w-4 mr-2" />
                                  )}
                                  Speichern
                                </Button>
                              </>
                            ) : (
                              <>
                                {/* A visible button with text, not a bare pencil (Mike 2026-10-09, Befund 22: he
                                    thought the posts could not be edited). */}
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="border-primary text-primary hover:bg-primary/10"
                                  onClick={() => handleEdit(post)}
                                  data-testid="button-edit-post"
                                >
                                  <Edit2 className="h-4 w-4 mr-2" />
                                  Bearbeiten
                                </Button>
                                {(post as any).postingStatus !== "posted" && (post as any).postingStatus !== "scheduled" && post.approvalStatus !== "rejected" && (
                                  <>
                                    <Button
                                      variant="destructive"
                                      size="sm"
                                      onClick={() => handleReject(post)}
                                      disabled={approvePostMutation.isPending || publishPostMutation.isPending || schedulePostMutation.isPending}
                                      data-testid="button-reject-post"
                                    >
                                      <X className="h-4 w-4 mr-2" />
                                      Ablehnen
                                    </Button>

                                    {/* Schedule button with popover */}
                                    <Popover open={scheduleOpen} onOpenChange={(open) => { setScheduleOpen(open); if (!open) setCustomTimeOpen(false) }}>
                                      <PopoverTrigger asChild>
                                        <Button
                                          size="sm"
                                          className="bg-amber-500 hover:bg-amber-600 text-white"
                                          disabled={schedulePostMutation.isPending || publishPostMutation.isPending}
                                          data-testid="button-schedule-post"
                                        >
                                          {schedulePostMutation.isPending ? (
                                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                          ) : (
                                            <CalendarClock className="h-4 w-4 mr-2" />
                                          )}
                                          Planen
                                        </Button>
                                      </PopoverTrigger>
                                      <PopoverContent className="w-80 p-0" align="end">
                                        {!customTimeOpen ? (
                                          <div className="p-3 space-y-1">
                                            <p className="text-sm font-medium text-muted-foreground px-2 pb-2">
                                              Optimale Zeiten · {platformConfig[post.platform as keyof typeof platformConfig]?.name || post.platform} · Berliner Zeit
                                            </p>
                                            {getSuggestedTimes(post.platform as any, scheduledTimesByPlatform[post.platform] || []).map((slot, i) => (
                                              <button
                                                key={i}
                                                className={`w-full flex items-center justify-between px-3 py-2 text-sm rounded-md transition-colors text-left ${slot.isTaken ? 'opacity-40 cursor-not-allowed' : 'hover:bg-accent'}`}
                                                onClick={() => !slot.isTaken && handleSchedule(post, slot.date)}
                                                disabled={schedulePostMutation.isPending || slot.isTaken}
                                              >
                                                <div className="flex items-center gap-2">
                                                  <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                                                  <span className={slot.isTaken ? 'line-through' : ''}>{slot.label}</span>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                  {slot.isTaken && (
                                                    <span className="text-xs text-muted-foreground">belegt</span>
                                                  )}
                                                  {slot.isPeak && !slot.isTaken && (
                                                    <Badge variant="secondary" className="text-xs gap-1 py-0">
                                                      <Star className="h-3 w-3 fill-amber-500 text-amber-500" />
                                                      Peak
                                                    </Badge>
                                                  )}
                                                </div>
                                              </button>
                                            ))}
                                            <div className="border-t mt-2 pt-2">
                                              <button
                                                className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-md hover:bg-accent transition-colors text-left text-muted-foreground"
                                                onClick={() => setCustomTimeOpen(true)}
                                              >
                                                <CalendarClock className="h-3.5 w-3.5" />
                                                Eigene Zeit wählen
                                              </button>
                                            </div>
                                          </div>
                                        ) : (
                                          <div className="p-3 space-y-3">
                                            <div className="flex items-center justify-between">
                                              <p className="text-sm font-medium">Eigene Zeit wählen</p>
                                              <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => setCustomTimeOpen(false)}>
                                                <X className="h-3.5 w-3.5" />
                                              </Button>
                                            </div>
                                            <Calendar
                                              mode="single"
                                              selected={customDate}
                                              onSelect={setCustomDate}
                                              locale={de}
                                              disabled={(date) => {
                                                const today = new Date()
                                                today.setHours(0, 0, 0, 0)
                                                return date < today || date.getDay() === 0 || date.getDay() === 6
                                              }}
                                              className="rounded-md border"
                                            />
                                            <div className="flex items-center gap-2">
                                              <Label className="text-sm text-muted-foreground whitespace-nowrap">Uhrzeit:</Label>
                                              <select
                                                value={customHour}
                                                onChange={(e) => setCustomHour(e.target.value)}
                                                className="flex h-9 w-full rounded-md border border-input bg-background px-2 py-1 text-sm"
                                              >
                                                {Array.from({ length: 24 }, (_, i) => (
                                                  <option key={i} value={String(i).padStart(2, "0")}>
                                                    {String(i).padStart(2, "0")}
                                                  </option>
                                                ))}
                                              </select>
                                              <span className="text-muted-foreground">:</span>
                                              <select
                                                value={customMinute}
                                                onChange={(e) => setCustomMinute(e.target.value)}
                                                className="flex h-9 w-full rounded-md border border-input bg-background px-2 py-1 text-sm"
                                              >
                                                {["00", "15", "30", "45"].map((m) => (
                                                  <option key={m} value={m}>{m}</option>
                                                ))}
                                              </select>
                                            </div>
                                            <Button
                                              size="sm"
                                              className="w-full"
                                              onClick={() => handleCustomSchedule(post)}
                                              disabled={!customDate || schedulePostMutation.isPending}
                                            >
                                              {schedulePostMutation.isPending ? (
                                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                              ) : (
                                                <CalendarClock className="h-4 w-4 mr-2" />
                                              )}
                                              Planen
                                            </Button>
                                          </div>
                                        )}
                                      </PopoverContent>
                                    </Popover>

                                    <Button
                                      size="sm"
                                      onClick={() => handlePublish(post)}
                                      disabled={publishPostMutation.isPending || schedulePostMutation.isPending}
                                      data-testid="button-publish-post"
                                    >
                                      {publishPostMutation.isPending ? (
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                      ) : (
                                        <Check className="h-4 w-4 mr-2" />
                                      )}
                                      Freigeben
                                    </Button>
                                  </>
                                )}
                                {post.approvalStatus === "approved" && (post as any).postingStatus !== "posted" && (post as any).postingStatus !== "scheduled" && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleReject(post)}
                                    disabled={approvePostMutation.isPending}
                                    data-testid="button-withdraw-post"
                                  >
                                    <X className="h-4 w-4 mr-2" />
                                    Zurückziehen
                                  </Button>
                                )}
                                {post.approvalStatus === "rejected" && (
                                  <Button
                                    size="sm"
                                    onClick={() => handlePublish(post)}
                                    disabled={publishPostMutation.isPending}
                                    data-testid="button-reapprove-post"
                                  >
                                    {publishPostMutation.isPending ? (
                                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    ) : (
                                      <Check className="h-4 w-4 mr-2" />
                                    )}
                                    Doch freigeben
                                  </Button>
                                )}
                                {((post as any).postingStatus === "posted" || (post as any).postingStatus === "scheduled") && (
                                  <>
                                    <Popover open={scheduleOpen} onOpenChange={(open) => { setScheduleOpen(open); if (!open) setCustomTimeOpen(false) }}>
                                      <PopoverTrigger asChild>
                                        <Button variant="outline" size="sm" disabled={schedulePostMutation.isPending || publishPostMutation.isPending}>
                                          {schedulePostMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CalendarClock className="h-4 w-4 mr-2" />}
                                          Neu planen
                                        </Button>
                                      </PopoverTrigger>
                                      <PopoverContent className="w-80 p-0" align="end">
                                        {!customTimeOpen ? (
                                          <div className="p-3 space-y-1">
                                            <p className="text-sm font-medium text-muted-foreground px-2 pb-2">
                                              Optimale Zeiten · {platformConfig[post.platform as keyof typeof platformConfig]?.name || post.platform} · Berliner Zeit
                                            </p>
                                            {getSuggestedTimes(post.platform as any, scheduledTimesByPlatform[post.platform] || []).map((slot, i) => (
                                              <button
                                                key={i}
                                                className={`w-full flex items-center justify-between px-3 py-2 text-sm rounded-md transition-colors text-left ${slot.isTaken ? 'opacity-40 cursor-not-allowed' : 'hover:bg-accent'}`}
                                                onClick={() => !slot.isTaken && handleSchedule(post, slot.date)}
                                                disabled={schedulePostMutation.isPending || slot.isTaken}
                                              >
                                                <div className="flex items-center gap-2">
                                                  <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                                                  <span className={slot.isTaken ? 'line-through' : ''}>{slot.label}</span>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                  {slot.isTaken && <span className="text-xs text-muted-foreground">belegt</span>}
                                                  {slot.isPeak && !slot.isTaken && (
                                                    <Badge variant="secondary" className="text-xs gap-1 py-0">
                                                      <Star className="h-3 w-3 fill-amber-500 text-amber-500" />
                                                      Peak
                                                    </Badge>
                                                  )}
                                                </div>
                                              </button>
                                            ))}
                                            <div className="border-t mt-2 pt-2">
                                              <button className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-md hover:bg-accent transition-colors text-left text-muted-foreground" onClick={() => setCustomTimeOpen(true)}>
                                                <CalendarClock className="h-3.5 w-3.5" />
                                                Eigene Zeit wählen
                                              </button>
                                            </div>
                                          </div>
                                        ) : (
                                          <div className="p-3 space-y-3">
                                            <div className="flex items-center justify-between">
                                              <p className="text-sm font-medium">Eigene Zeit wählen</p>
                                              <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => setCustomTimeOpen(false)}>
                                                <X className="h-3.5 w-3.5" />
                                              </Button>
                                            </div>
                                            <Calendar mode="single" selected={customDate} onSelect={setCustomDate} locale={de} disabled={(date) => { const today = new Date(); today.setHours(0,0,0,0); return date < today || date.getDay() === 0 || date.getDay() === 6 }} className="rounded-md border" />
                                            <div className="flex items-center gap-2">
                                              <Label className="text-sm text-muted-foreground whitespace-nowrap">Uhrzeit:</Label>
                                              <select value={customHour} onChange={(e) => setCustomHour(e.target.value)} className="flex h-9 w-full rounded-md border border-input bg-background px-2 py-1 text-sm">
                                                {Array.from({ length: 24 }, (_, i) => (<option key={i} value={String(i).padStart(2, "0")}>{String(i).padStart(2, "0")}</option>))}
                                              </select>
                                              <span className="text-muted-foreground">:</span>
                                              <select value={customMinute} onChange={(e) => setCustomMinute(e.target.value)} className="flex h-9 w-full rounded-md border border-input bg-background px-2 py-1 text-sm">
                                                {["00", "15", "30", "45"].map((m) => (<option key={m} value={m}>{m}</option>))}
                                              </select>
                                            </div>
                                            <Button size="sm" className="w-full" onClick={() => handleCustomSchedule(post)} disabled={!customDate || schedulePostMutation.isPending}>
                                              {schedulePostMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CalendarClock className="h-4 w-4 mr-2" />}
                                              Planen
                                            </Button>
                                          </div>
                                        )}
                                      </PopoverContent>
                                    </Popover>
                                    <Button variant="outline" size="sm" onClick={() => handlePublish(post)} disabled={publishPostMutation.isPending || schedulePostMutation.isPending}>
                                      {publishPostMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RotateCw className="h-4 w-4 mr-2" />}
                                      Erneut posten
                                    </Button>
                                  </>
                                )}
                              </>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    </TabsContent>
                  )
                })}
              </Tabs>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
